import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Button } from "../ui/button";
import { glassCard, glassCardHover, glassPanel } from "../../utils/glassStyles";

export function LegalCard({ children, className = "" }) {
  return (
    <div className={`rounded-2xl ${glassCard} ${className}`}>{children}</div>
  );
}

export function LegalFeatureCard({ icon: Icon, title, description }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -4 }}
      className={`text-center rounded-2xl p-6 ${glassCardHover}`}
    >
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-primary/10 mb-4">
        <Icon className="w-6 h-6 text-primary" />
      </div>
      <h3 className="font-semibold mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground">{description}</p>
    </motion.div>
  );
}

export default function LegalPageShell({
  title,
  subtitle,
  icon: Icon,
  badges = [],
  children,
  relatedLinks = [],
}) {
  return (
    <div className="min-h-screen pb-16">
      <section className="relative overflow-hidden py-12 sm:py-16 md:py-20">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-gradient-to-b from-primary/15 via-accent/10 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button variant="ghost" size="sm" asChild className="mb-6 -ml-2">
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              Back to home
            </Link>
          </Button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent shadow-lg mb-5">
              <Icon className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4 text-brand-gradient">
              {title}
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto mb-6 leading-relaxed">
              {subtitle}
            </p>
            {badges.length > 0 && (
              <div className="flex flex-wrap justify-center gap-2">
                {badges.map((badge) => (
                  <span
                    key={badge.label}
                    className={`px-3 py-1 rounded-full text-xs font-medium ${badge.className}`}
                  >
                    {badge.label}
                  </span>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        {children}

        {relatedLinks.length > 0 && (
          <div className={`rounded-2xl p-6 sm:p-8 text-center ${glassPanel}`}>
            <p className="text-sm text-muted-foreground mb-4">Related policies</p>
            <div className="flex flex-wrap justify-center gap-3">
              {relatedLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="px-5 py-2.5 rounded-xl border border-primary/20 bg-background/50 text-primary font-medium text-sm hover:bg-primary/5 transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
